<template>
  <div class="py-4 text-slate-800 animate-fade-in">
    <!-- SEZIONE: CONFIGURAZIONE MODERAZIONE CHAT -->
    <div
      class="w-full mb-6 bg-white p-3 rounded-2xl shadow-sm border border-slate-100 flex flex-col md:flex-row md:items-end justify-between gap-6"
    >
      <div class="flex-1">
        <h3
          class="font-bold text-slate-900 mb-3 text-lg flex items-center gap-2"
        >
          <i class="fa-solid fa-comments-text text-blue-500"></i> Moderation
          Chat Settings
        </h3>
        <div class="grid grid-cols-2 gap-3 w-full sm:grid-cols-4">
          <div
            v-for="(value, key) in allowedContent"
            :key="key"
            class="flex items-center gap-3 bg-slate-50 p-2.5 rounded-xl border border-slate-100 transition-all hover:bg-slate-100/70"
            :class="{
              'opacity-40 pointer-events-none bg-slate-100':
                !allowedContent.is_enabled && key !== 'is_enabled',
            }"
          >
            <input
              type="checkbox"
              v-model="allowedContent[key]"
              :id="key"
              class="w-4 h-4 text-blue-600 bg-white border-slate-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer disabled:cursor-not-allowed"
              :disabled="!allowedContent.is_enabled && key !== 'is_enabled'"
            />
            <label
              :for="key"
              class="text-sm font-semibold text-slate-700 capitalize select-none cursor-pointer text-nowrap"
            >
              {{ key.replace("_", " ") }}
            </label>
          </div>
        </div>
      </div>

      <div class="self-end">
        <button
          @click="saveNewModeration"
          class="w-full md:w-auto px-6 py-2.5 text-sm font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-100 transition-all shadow-sm"
        >
          Save Settings
        </button>
      </div>
    </div>

    <div
      class="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 bg-slate-50 p-3 rounded-2xl border border-slate-100"
    >
      <div class="flex-grow">
        <Filters
          :options="filterOptions"
          :showActionButton="true"
          :search="search"
          :resetFilter="resetFilter"
        />
      </div>

      <div class="flex justify-end shrink-0 px-2 lg:px-0">
        <button
          @click="addNewBannedWord"
          class="w-full lg:w-auto flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 focus:ring-4 focus:ring-emerald-100 transition-all shadow-sm"
        >
          <i class="fa-solid fa-plus text-xs"></i>
          Add Word
        </button>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="flex-grow flex flex-col items-center justify-center p-20"
    >
      <Loader />
    </div>

    <div
      v-else
      class="table-container-custom bg-white shadow-sm rounded-2xl overflow-hidden border border-1 mb-3 mt-3"
    >
      <DataTable
        :value="tableData"
        :lazy="true"
        :paginator="true"
        :rows="filters.limit"
        :totalRecords="totalPages * filters.limit"
        :loading="isLoading"
        :first="(filters.page - 1) * filters.limit"
        @page="onPage"
        @sort="onSort"
        :sortField="sortField"
        :sortOrder="sortOrder"
        :rowsPerPageOptions="[15, 25, 50, 100]"
        removableSort
        stripedRows
        scrollable
        scrollHeight="flex"
        responsiveLayout="stack"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        class="p-datatable-sm"
      >
        <template #empty>
          <div class="text-center py-8 text-slate-400 font-medium">
            No banned words found.
          </div>
        </template>

    
        <Column
          field="word"
          header="Word"
          :sortable="true"
          class="font-medium text-slate-700"
        ></Column>

       
        <Column field="action_column" header="Action" class="w-24 text-center">
          <template #body="slotProps">
            <button
              @click="removeRow(slotProps.data)"
              class="p-2 text-rose-500 w-6 h-6 flex items-center justify-center hover:text-rose-700 hover:bg-rose-50 rounded-full transition-all"
              title="Delete Word"
            >
              <i class="fa-solid fa-trash text-base text-[10px] p-1"></i>
            </button>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getFetch, postFetch, deleteRequest } from "@/api/axiosClient";
import Filters from "@/slot/components/table/Filters.vue";
import Swal from "sweetalert2";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Loader from "@/components/Loader.vue";

export default {
  name: "ModerationChatManager",
  components: {
    Filters,
    DataTable,
    Column,
    Loader,
  },
  props: {
    skinFilt: {
      type: Number,
      required: true,
      default: 0,
    },
    category: {
      type: String,
      default: "slot",
    },
    database: {
      type: String,
      default: "theearcasino",
    },
  },
  data() {
    return {
      allowedContent: {
        is_enabled: true,
        ban_emails: true,
        ban_phones: true,
        ban_urls: true,
      },

      filters: {
        searchedWord: null,
        page: 1,
        limit: 15,
      },

      tableData: [],
      totalPages: 0,
      isLoading: true,
      sortField: null,
      sortOrder: null,

      filterOptions: [
        {
          label: "Word",
          type: "text",
          placeholder: "Search for Word",
          property: "searchedWord",
        },
      ],

      limit: [
        { label: "15", value: 15, selected: true },
        { label: "25", value: 25, selected: false },
        { label: "50", value: 50, selected: false },
        { label: "100", value: 100, selected: false },
      ],
    };
  },
  computed: {
    ...mapGetters(["isMenuOpen"]),
    selectedLimit() {
      return this.filters.limit;
    },
  },
  async mounted() {
    await this.getModerationSettings();
    await this.search();
  },
  methods: {
    async search() {
      this.isLoading = true;
      try {
        const data = await getFetch(
          `words`,
          {
            page: this.filters.page,
            limit: this.filters.limit,
            skin_id: this.skinFilt,
            word: this.filters.searchedWord,
          },
          true,
          this.database
        );
        this.tableData = data.data ?? [];
        this.totalPages = data.totalPages ?? 0;

        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 250);
      } catch (error) {
        console.error("Errore durante il fetch dei dati:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async resetFilter() {
      this.filters.searchedWord = null;
      this.filters.limit = 15;
      this.filters.page = 1;
      this.sortField = null;
      this.sortOrder = null;

      this.limit.forEach((option) => {
        option.selected = option.value === 15;
      });

      await this.search();
    },

    async addNewBannedWord() {
      const { value: newWord } = await Swal.fire({
        title: "Add new word to block list",
        input: "text",
        inputPlaceholder: "Type the word here...",
        showCancelButton: true,
        confirmButtonColor: "#10b981", // Emerald-600
        inputValidator: (value) => {
          if (!value) return "You must insert a word!";
          if (/\s/.test(value)) return "Spaces are not allowed in the word!";
        },
      });

      if (!newWord) return;

      try {
        const response = await postFetch(
          "words",
          {
            word: newWord.trim(),
            skin_id: this.skinFilt,
            key: "moderationchat",
          },
          true,
          this.database
        );

        if (response.status === 200) {
          Swal.fire(
            "Success",
            "The word has been added successfully.",
            "success",
          );
          this.search();
        }
      } catch (error) {
        const errMsg =
          error.response?.status === 409
            ? error.response.data.error
            : "Something went wrong...";
        Swal.fire("Error!", errMsg, "error");
      }
    },

    async removeRow(row) {
      const confirmResult = await Swal.fire({
        title: "Are you sure?",
        text: `Do you want to unban the word "${row.word}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        confirmButtonColor: "#ef4444", // Rose-500
      });

      if (!confirmResult.isConfirmed) return;

      try {
        const response = await deleteRequest(
          "words",
          {
            word: row.word,
            skin_id: this.skinFilt,
          },
          this.database
        );

        if (response?.status === 200) {
          await Swal.fire(
            "Deleted!",
            `The word "${row.word}" has been unbanned.`,
            "success",
          );
          this.search();
        } else {
          throw new Error("Deletion failed");
        }
      } catch (error) {
        console.error("Delete error:", error);
        await Swal.fire(
          "Error!",
          "Something went wrong while deleting the word.",
          "error",
        );
      }
    },

    async getModerationSettings() {
      try {
        const data = await getFetch("skins/" + this.skinFilt, {}, true, this.database);
        this.allowedContent.ban_emails = data.chat.ban_emails;
        this.allowedContent.ban_phones = data.chat.ban_phones;
        this.allowedContent.ban_urls = data.chat.ban_urls;
        this.allowedContent.is_enabled = data.chat.is_enabled ?? true;
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    },

    async saveNewModeration() {
      const confirm = await Swal.fire({
        title: "Confirm Update",
        text: "Are you sure you want to update the moderation settings?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, update it",
      });

      if (!confirm.isConfirmed) return;

      try {
        const updateModeration = await postFetch(
          "/skins/updateModeration",
          {
            ban_phones: this.allowedContent.ban_phones,
            ban_emails: this.allowedContent.ban_emails,
            ban_urls: this.allowedContent.ban_urls,
            skin_id: this.skinFilt,
            is_enabled: this.allowedContent.is_enabled,
          },
          true,
          this.database
        );

        if (updateModeration?.status === 200) {
          await Swal.fire({
            title: "Updated!",
            text: "Moderation settings updated successfully.",
            icon: "success",
          });
        } else {
          throw new Error("Update failed");
        }
      } catch (error) {
        console.error("Error updating moderation:", error);
        await Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Something went wrong while updating settings.",
        });
      }
    },

    onPage(event) {
      this.filters.page = event.page + 1;
      this.filters.limit = event.rows;

      this.limit.forEach((option) => {
        option.selected = option.value === event.rows;
      });
      this.search();
    },

    onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder;
      this.search();
    },
  },
};
</script>
